/**
 * App roles for Cloudflare-native JWT users (user | admin | staff).
 *
 * Prefer `role` on the mapped auth user / JWT claims. Legacy metadata-shaped
 * objects still resolve app_metadata.role then user_metadata.role.
 */

export const ROLES = {
  USER: "user",
  ADMIN: "admin",
  STAFF: "staff",
};

const LEGACY_TO_APP = {
  seeker: ROLES.USER,
  teacher: ROLES.ADMIN,
  editor: ROLES.STAFF,
};

/** Normalize seeker/teacher (and editor) to user/admin/staff. */
export function normalizeRole(role) {
  if (!role || typeof role !== "string") return ROLES.USER;
  const r = role.trim().toLowerCase();
  if (r === ROLES.USER || r === ROLES.ADMIN || r === ROLES.STAFF) return r;
  return LEGACY_TO_APP[r] || ROLES.USER;
}

/** Alias used by AuthContext / Worker-aligned naming. */
export const normalizeAppRole = normalizeRole;

/** Resolve role from auth user object or legacy metadata-shaped payload. */
export function getRoleFromAuthUser(user) {
  if (!user) return ROLES.USER;
  if (typeof user.role === "string" && user.role.trim()) {
    return normalizeRole(user.role);
  }
  const appMeta = user.app_metadata || {};
  const userMeta = user.user_metadata || {};
  const appRole = typeof appMeta.role === "string" ? appMeta.role : "";
  const userRole = typeof userMeta.role === "string" ? userMeta.role : "";
  return normalizeRole(appRole || userRole);
}

export function isAdminRole(role) {
  const r = normalizeRole(role);
  return r === ROLES.ADMIN;
}

export function isStaffOrAdminRole(role) {
  const r = normalizeRole(role);
  return r === ROLES.ADMIN || r === ROLES.STAFF;
}

/** Post-login redirect: admin/staff → /admin, everyone else → /dashboard. */
export function loginRedirectPath(role) {
  return isStaffOrAdminRole(role) ? "/admin" : "/dashboard";
}

export function canAccessAdminRoutes(role) {
  return isStaffOrAdminRole(role);
}

export function canAccessDashboard(role) {
  return !!role || role === ROLES.USER || isStaffOrAdminRole(role);
}

/** Roles allowed for public self-signup (always user). */
export function signupRole(inputRole) {
  const r = normalizeRole(inputRole);
  return r === ROLES.ADMIN || r === ROLES.STAFF ? ROLES.USER : ROLES.USER;
}

/** Authorization helper: true when JWT role grants cross-user access. */
export function canManageAll(role) {
  return isStaffOrAdminRole(role);
}

/** @deprecated Use canManageAll */
export const rlsCanManageAll = canManageAll;

/** Authorization helper: normal users only touch own rows. */
export function canAccessOwnData(requestUserId, authUserId) {
  if (!authUserId) return false;
  return requestUserId === authUserId;
}

/** @deprecated Use canAccessOwnData */
export const rlsCanAccessOwnData = canAccessOwnData;
